import React from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Alert,
} from '@mui/material';
import { AccountDummyDetails } from '../hooks/useAccountDetails';
import { StatusBadge } from '../../../../../../shared/components/ui/StatusBadge';

interface Props {
  details: AccountDummyDetails;
}

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    sx={{
      fontSize: '13px',
      fontWeight: 600,
      lineHeight: '18px',
      letterSpacing: '0.05em',
      color: '#434656',
      textTransform: 'uppercase',
      mb: 2,
    }}
  >
    {children}
  </Typography>
);

const cardSx = {
  border: '1px solid #E2E8F0',
  borderRadius: '12px',
  p: 3,
  bgcolor: '#ffffff',
  boxShadow: '0px 1px 3px rgba(0,0,0,0.05), 0px 10px 15px -5px rgba(0,0,0,0.03)',
};

const tableCellHeaderSx = {
  fontSize: '12px',
  fontWeight: 500,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.05em',
  color: '#64748b',
  bgcolor: '#f8fafc',
  borderBottom: '1px solid #E2E8F0',
  py: '12px',
};

const tableCellSx = {
  fontSize: '14px',
  color: '#0b1c30',
  py: '12px',
  borderBottom: '1px solid #f1f5f9',
};

export const AccountDetailsSupportTab: React.FC<Props> = ({ details }) => {
  return (
    <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box sx={cardSx}>
        <SectionTitle>Tickets</SectionTitle>
        <Box sx={{ display: 'flex', gap: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography
              sx={{
                fontSize: '12px',
                fontWeight: 500,
                color: '#64748b',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}
            >
              Open
            </Typography>
            <StatusBadge label={String(details.openTickets)} status="suspended" />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography
              sx={{
                fontSize: '12px',
                fontWeight: 500,
                color: '#64748b',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}
            >
              Resolved
            </Typography>
            <StatusBadge label={String(details.resolvedTickets)} status="active" />
          </Box>
        </Box>
      </Box>

      <Box sx={cardSx}>
        <SectionTitle>Internal Notes</SectionTitle>
        <Typography sx={{ fontSize: '14px', color: details.internalNotes ? '#0b1c30' : '#94a3b8' }}>
          {details.internalNotes || 'No notes'}
        </Typography>
      </Box>

      <Box sx={cardSx}>
        <SectionTitle>Impersonation</SectionTitle>
        {details.impersonationEnabled ? (
          <Alert severity="warning">Impersonation is currently active for this account.</Alert>
        ) : (
          <Typography sx={{ fontSize: '14px', color: '#64748b' }}>
            Impersonation is not active.
          </Typography>
        )}
      </Box>

      <Box sx={{ ...cardSx, p: 0, overflow: 'hidden' }}>
        <Box sx={{ px: 3, pt: 3, pb: 2 }}>
          <SectionTitle>Audit Log</SectionTitle>
        </Box>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={tableCellHeaderSx}>Date</TableCell>
              <TableCell sx={tableCellHeaderSx}>Action</TableCell>
              <TableCell sx={tableCellHeaderSx}>Actor</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {details.auditLog.map((entry, index) => (
              <TableRow
                key={index}
                sx={{ '&:last-child td': { borderBottom: 0 }, '&:hover': { bgcolor: '#f8fafc' } }}
              >
                <TableCell sx={tableCellSx}>{entry.date}</TableCell>
                <TableCell sx={tableCellSx}>{entry.action}</TableCell>
                <TableCell sx={tableCellSx}>{entry.actor}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
};
